import { UserType } from '../types'

export type TourTarget =
  | 'sidebar:departments'
  | 'sidebar:users'
  | 'sidebar:settings'
  | 'sidebar:subscription'
  | 'sidebar:audit'
  | 'sidebar:roleTypes'
  | 'sidebar:resources'
  | 'sidebar:customers'
  | 'sidebar:projects'
  | 'sidebar:partners'
  | 'sidebar:partnerRates'
  | 'sidebar:allocations'
  | 'sidebar:tasks'
  | 'sidebar:weektimesheet'
  | 'sidebar:tsApprovals'
  | 'sidebar:timesheets'
  | 'sidebar:tsHistory'
  | 'sidebar:timeoff'
  | 'sidebar:holidays'
  | 'sidebar:currency'
  | 'sidebar:dashboard'
  | 'sidebar:governance'
  | 'sidebar:reports'
  | 'sidebar:gantt'
  | 'admin:overview'
  | 'admin:departments'
  | 'admin:users'
  | 'admin:settings'
  | 'admin:subscription'
  | 'admin:audit'
  | 'admin:legal'
  | 'admin:observability'
  | 'admin:releases'
  | 'report-tab:projectanalysis'
  | 'report-tab:profitability'
  | 'report-tab:utilization'
  | 'report-tab:revenue'
  | 'report-tab:forecastvactual'
  | 'report-tab:nonbillable'
  | 'report-tab:gantt'
  | 'content:general'

export interface TourStep {
  id: string
  target: TourTarget
  selector?: string
  title: string
  description: string
  cta?: string
  tabId?: string
  adminSection?: string
  roles: UserType[]
  /** Optional numbered list items rendered below the description */
  listItems?: string[]
}

export const tourSteps: TourStep[] = [
  {
    id: 'welcome',
    target: 'content:general',
    title: 'Welcome to AllocatIQ',
    description: 'This guided tour will walk you through the key concepts and features of the application, step by step. You will learn how to set up your master data, manage resources and projects, and track time. Let us get started.',
    roles: ['owner', 'admin', 'manager', 'contributor', 'viewer', 'resource'],
  },

  // ─── Master Data Concept ───
  {
    id: 'master-data-concept',
    target: 'content:general',
    title: 'What is Master Data?',
    description: 'Before using AllocatIQ, you need to set up your master data. These are the core records everything else depends on. Without them, you cannot create allocations, assign tasks, or track time.',
    listItems: [
      'Role Types -- define skills and rate cards',
      'Resources -- your team members, each needing a Role Type',
      'Customers -- the clients you do work for',
      'Projects -- work engagements tied to a Customer',
      'Partners -- external organizations you collaborate with',
      'Partner Rate Sheets -- pricing per role for each Partner',
      'Holidays -- non-working days that affect timesheets',
      'Currency -- the currencies used for billing and reporting',
    ],
    roles: ['owner', 'admin', 'manager', 'contributor', 'viewer', 'resource'],
  },
  {
    id: 'master-data-sequence',
    target: 'content:general',
    title: 'The Setup Sequence',
    description: 'Master data must be set up in this order because each item depends on the ones before it. We will walk through each step now.',
    listItems: [
      'Role Types -- define skills, cost rates, and billing rates',
      'Resources -- assign each person a Role Type',
      'Customers -- create client records before Projects',
      'Projects -- link to a Customer, set dates and department',
      'Partners -- create external organizations',
      'Partner Rate Sheets -- price each role for each Partner',
      'Holidays -- set non-working days for timesheet accuracy',
      'Currency -- configure currencies for billing and reports',
    ],
    roles: ['owner', 'admin', 'manager', 'contributor', 'viewer', 'resource'],
  },

  // ─── Admin Panel (top-to-bottom order matching nav) ───
  {
    id: 'admin-overview',
    target: 'admin:overview',
    title: 'Admin Overview',
    description: 'The Admin Overview shows your plan limits and current usage at a glance -- how many users, projects, and resources you have used. This is your starting point for managing your organization.',
    adminSection: 'overview',
    roles: ['owner', 'admin'],
  },
  {
    id: 'admin-subscription',
    target: 'admin:subscription',
    title: 'Subscription & Billing',
    description: 'Manage your plan, upgrade or downgrade, and view billing history. If you are on a trial, you can choose a paid plan here before the trial expires.',
    adminSection: 'subscription',
    roles: ['owner'],
  },
  {
    id: 'admin-users',
    target: 'admin:users',
    title: 'Users & Roles',
    description: 'Here you manage team members, send invitations, and assign roles. Each member gets a user type: owner, admin, manager, contributor, or viewer. You can also assign them to a department so they only see relevant data.',
    cta: 'Click "Users & Roles" to invite a team member.',
    adminSection: 'users',
    roles: ['owner', 'admin'],
  },
  {
    id: 'admin-departments',
    target: 'admin:departments',
    title: 'Departments',
    description: 'Departments organize your team into groups for access control and data segmentation. Create departments so you can assign team members and resources to the right areas.',
    cta: 'Click "Departments" to create your first department.',
    adminSection: 'departments',
    roles: ['owner', 'admin'],
  },
  {
    id: 'admin-settings',
    target: 'admin:settings',
    title: 'Organization Settings',
    description: 'Configure your organization name and default currency here. These settings affect how budgets and reports are displayed across the application.',
    adminSection: 'settings',
    roles: ['owner', 'admin'],
  },
  {
    id: 'admin-audit',
    target: 'admin:audit',
    title: 'Audit Log',
    description: 'Every sensitive action -- creating projects, changing roles, deleting records -- is tracked here. Use this to monitor activity and maintain accountability across your organization.',
    adminSection: 'audit',
    roles: ['owner', 'admin'],
  },
  {
    id: 'admin-legal',
    target: 'admin:legal',
    title: 'Legal & Compliance',
    description: 'Track legal document acceptances and compliance status for your organization. This ensures all team members have accepted the required terms and policies.',
    adminSection: 'legal',
    roles: ['owner', 'admin'],
  },
  {
    id: 'admin-observability',
    target: 'admin:observability',
    title: 'Observability',
    description: 'Monitor system health, API performance, and error rates here. This is useful for diagnosing issues and ensuring the application runs smoothly.',
    adminSection: 'observability',
    roles: ['owner', 'admin'],
  },
  {
    id: 'admin-releases',
    target: 'admin:releases',
    title: 'Release Notes',
    description: 'Stay up to date with the latest features and improvements. Each release note describes what changed, what was added, and any fixes applied.',
    adminSection: 'releases',
    roles: ['owner', 'admin'],
  },

  // ─── Master Data Setup (in required sequence order) ───
  // 1. Role Types
  {
    id: 'role-types',
    target: 'sidebar:roleTypes',
    title: 'Step 1: Role Types',
    description: 'Role types define the skills and positions your resources can hold, such as Developer, Project Manager, or QA Engineer. Each role type has a cost rate and a billing rate. This is the first master data you must set up because resources depend on role types, and partner rate sheets use them for pricing.',
    cta: 'Click "Role Types" in the sidebar to create your first role type.',
    tabId: 'roleTypes',
    roles: ['owner', 'admin', 'manager'],
  },

  // 2. Resources
  {
    id: 'resources',
    target: 'sidebar:resources',
    title: 'Step 2: Resources',
    description: 'Resources are the people who will be assigned to projects. Each resource must be assigned a Role Type (created in the previous step), have their availability set, and be assigned to a department. Resources cannot be created without a Role Type.',
    cta: 'Click "Resources" in the sidebar to add a resource.',
    tabId: 'resources',
    roles: ['owner', 'admin', 'manager', 'contributor'],
  },
  {
    id: 'resources-view',
    target: 'sidebar:resources',
    title: 'Resources',
    description: 'Resources are the people assigned to projects. Here you can view all resources, their roles, availability, and department assignments.',
    cta: 'Click "Resources" in the sidebar to view your team.',
    tabId: 'resources',
    roles: ['viewer'],
  },

  // 3. Customers
  {
    id: 'customers',
    target: 'sidebar:customers',
    title: 'Step 3: Customers',
    description: 'Customers are the clients you do work for. Create a customer record with their name, industry, region, and account manager. Customers must be created before you can set up Projects, because every project is linked to a customer.',
    cta: 'Click "Customers" in the sidebar to create a customer.',
    tabId: 'customers',
    roles: ['owner', 'admin', 'manager', 'contributor'],
  },
  {
    id: 'customers-view',
    target: 'sidebar:customers',
    title: 'Customers',
    description: 'Customers are the clients you do work for. Here you can view all customer records and their details.',
    cta: 'Click "Customers" in the sidebar to view your customers.',
    tabId: 'customers',
    roles: ['viewer'],
  },

  // 4. Projects
  {
    id: 'projects',
    target: 'sidebar:projects',
    title: 'Step 4: Projects',
    description: 'Projects are work engagements tied to a Customer. Create a project, link it to a customer, set start and end dates, choose a project type, and assign a department. Projects must be created before you can allocate resources or create tasks.',
    cta: 'Click "Projects" in the sidebar to create a project.',
    tabId: 'projects',
    roles: ['owner', 'admin', 'manager', 'contributor'],
  },
  {
    id: 'projects-view',
    target: 'sidebar:projects',
    title: 'Projects',
    description: 'Projects are the work engagements tied to customers. Here you can view all projects, their status, dates, and assigned departments.',
    cta: 'Click "Projects" in the sidebar to view your projects.',
    tabId: 'projects',
    roles: ['viewer'],
  },

  // 5. Partners
  {
    id: 'partners',
    target: 'sidebar:partners',
    title: 'Step 5: Partners',
    description: 'Partners are external organizations you collaborate with -- subcontractors, outsourcing firms, or staffing agencies. Create a partner record before setting up Partner Rate Sheets, because each rate sheet must be linked to a partner.',
    cta: 'Click "Partners" in the sidebar to create a partner.',
    tabId: 'partners',
    roles: ['owner', 'admin', 'manager', 'contributor'],
  },
  {
    id: 'partners-view',
    target: 'sidebar:partners',
    title: 'Partners',
    description: 'Partners are external organizations you collaborate with. Here you can view all partner records and their details.',
    cta: 'Click "Partners" in the sidebar to view your partners.',
    tabId: 'partners',
    roles: ['viewer'],
  },

  // 6. Partner Rate Sheets
  {
    id: 'partner-rate-sheets',
    target: 'sidebar:partnerRates',
    title: 'Step 6: Partner Rate Sheets',
    description: 'Partner Rate Sheets define pricing for each Partner. A rate sheet is linked to a Partner and contains one entry per Role Type, specifying the billing rate, cost rate, and currency. This is how partner pricing works -- for each role a partner provides, you define what you charge the customer (billing rate) and what the partner costs you (cost rate).',
    cta: 'Click "Partner Rates" in the sidebar to set up rate sheets.',
    tabId: 'partnerRates',
    roles: ['owner', 'admin', 'manager', 'contributor'],
  },
  {
    id: 'partner-rate-sheets-view',
    target: 'sidebar:partnerRates',
    title: 'Partner Rate Sheets',
    description: 'Partner Rate Sheets define pricing for each Partner. Here you can view existing rate sheets and their rate entries for each role type.',
    cta: 'Click "Partner Rates" in the sidebar to view rate sheets.',
    tabId: 'partnerRates',
    roles: ['viewer'],
  },

  // 7. Holidays
  {
    id: 'holidays',
    target: 'sidebar:holidays',
    title: 'Step 7: Holidays',
    description: 'Holidays define the non-working days for your organization. These affect timesheet calculations -- time entries on holidays are handled differently, and resource availability is reduced on holiday dates. Set up your holiday calendar before resources start logging time.',
    cta: 'Click "Holidays" in the sidebar to set up holidays.',
    tabId: 'holidays',
    roles: ['owner', 'admin'],
  },

  // 8. Currency
  {
    id: 'currency',
    target: 'sidebar:currency',
    title: 'Step 8: Currency',
    description: 'Configure the currencies your organization operates in. This affects billing rates, cost rates, partner rate sheets, and financial reports. Set your default currency here to match the currency used in your contracts and invoices.',
    cta: 'Click "Currency" in the sidebar to configure currencies.',
    tabId: 'currency',
    roles: ['owner', 'admin'],
  },

  // ─── Allocation and Task Dependencies ───
  {
    id: 'allocations',
    target: 'sidebar:allocations',
    title: 'Allocations -- The Critical Link',
    description: 'Allocations connect Resources to Projects. Before a task can be assigned to any user, the resource must first be allocated to the project. Without an allocation, you cannot assign tasks, and users will not see the project in their timesheet. Allocations also define the start and end dates a resource is assigned to a project.',
    cta: 'Click "Allocations" in the sidebar to allocate resources to projects.',
    tabId: 'allocations',
    roles: ['owner', 'admin', 'manager', 'contributor'],
  },
  {
    id: 'allocations-view',
    target: 'sidebar:allocations',
    title: 'Allocations',
    description: 'Allocations connect resources to projects. Here you can view which resources are assigned to which projects, along with their allocation periods.',
    cta: 'Click "Allocations" in the sidebar to view allocations.',
    tabId: 'allocations',
    roles: ['viewer'],
  },

  // ─── Tasks (require allocations) ───
  {
    id: 'tasks',
    target: 'sidebar:tasks',
    title: 'Tasks',
    description: 'Tasks break down project work into trackable items. You can only assign a task to a resource who has been allocated to that project. Create tasks within a project, assign them to allocated resources, set estimated minutes, and track their status on the Kanban board.',
    cta: 'Click "Tasks" in the sidebar to create a task.',
    tabId: 'tasks',
    roles: ['owner', 'admin', 'manager', 'contributor'],
  },
  {
    id: 'tasks-view',
    target: 'sidebar:tasks',
    title: 'Tasks',
    description: 'Tasks break down project work into trackable items. Here you can view the task board and see which tasks are assigned to which resources.',
    cta: 'Click "Tasks" in the sidebar to view the task board.',
    tabId: 'tasks',
    roles: ['viewer'],
  },

  // ─── Timesheet date window explanation ───
  {
    id: 'timesheet-rules',
    target: 'content:general',
    title: 'Timesheet Rules',
    description: 'Users can only submit time within two date windows. If a date falls outside either window, timesheet entry for that date is blocked. This ensures all tracked time is valid and billable.',
    listItems: [
      'Project dates -- time cannot be logged before a project starts or after it ends',
      'Allocation dates -- a resource can only log time for dates they are allocated to the project',
    ],
    roles: ['owner', 'admin', 'manager', 'contributor', 'viewer', 'resource'],
  },

  // ─── Timesheets ───
  {
    id: 'week-timesheet',
    target: 'sidebar:weektimesheet',
    title: 'Weekly Timesheet',
    description: 'This is where resources log their time against projects and tasks, day by day. Select a resource and a week, then enter billable and non-billable hours for each project. Remember: you can only enter time within the project and allocation date windows. When done, submit the timesheet for approval.',
    cta: 'Click "Weekly Timesheet" in the sidebar to view the timesheet entry screen.',
    tabId: 'weektimesheet',
    roles: ['owner', 'admin', 'manager', 'contributor', 'viewer', 'resource'],
  },
  {
    id: 'ts-approvals',
    target: 'sidebar:tsApprovals',
    title: 'Timesheet Approvals',
    description: 'Managers review submitted timesheets here. You can approve or reject timesheets, and add a rejection reason if needed. Approved timesheets are locked from further editing.',
    cta: 'Click "Timesheet Approvals" in the sidebar to view pending approvals.',
    tabId: 'tsApprovals',
    roles: ['owner', 'admin', 'manager'],
  },
  {
    id: 'timesheets',
    target: 'sidebar:timesheets',
    title: 'Resource Timesheets',
    description: 'This is the manager view for submitting timesheets on behalf of resources. Select the resource from the dropdown whose timesheet you want to submit and then select the period for which you want to submit.',
    cta: 'Click "Resource Timesheets" in the sidebar to view the resource timesheet overview.',
    tabId: 'timesheets',
    roles: ['owner', 'admin', 'manager'],
  },
  {
    id: 'ts-history',
    target: 'sidebar:tsHistory',
    title: 'Timesheet History',
    description: 'This shows past timesheet records and submission history. Look up previous weeks, check who submitted and who approved, and review any rejection reasons.',
    cta: 'Click "Timesheet History" in the sidebar to view past timesheets.',
    tabId: 'tsHistory',
    roles: ['owner', 'admin', 'manager', 'contributor', 'viewer', 'resource'],
  },
  {
    id: 'time-off',
    target: 'sidebar:timeoff',
    title: 'Time Off',
    description: 'This is where users request time off -- vacation, sick leave, personal days, or unpaid leave. Managers can approve or reject requests from this same screen. Approved time off is reflected in resource availability.',
    cta: 'Click "Time Off" in the sidebar to submit a time-off request.',
    tabId: 'timeoff',
    roles: ['owner', 'admin', 'manager', 'contributor', 'viewer', 'resource'],
  },
  // ─── Reports ───
  {
    id: 'reports-overview',
    target: 'sidebar:reports',
    title: 'Reports',
    description: "The Reports section gives you a comprehensive view of your organization's financial and operational performance. Data in these reports comes from your master data, allocations, and timesheet entries -- so the more complete your setup, the more accurate the reports.",
    cta: 'Click "Reports" in the sidebar to view the reports.',
    tabId: 'reports',
    roles: ['owner', 'admin', 'manager'],
  },
  {
    id: 'reports-project-analysis',
    target: 'report-tab:projectanalysis',
    title: 'Project Analysis Report',
    description: 'This is the default report. It shows each project with its customer, status, type, number of resources, forecast hours, billable and non-billable hours, revenue, cost, profit, and actual timesheet hours.',
    listItems: [
      'Revenue comes from billable allocations multiplied by billing rates',
      'Cost comes from all allocations multiplied by cost rates',
      'Profit is Revenue minus Cost, shown in green or red',
      'Timesheet hours come from actual submitted timesheet entries',
      'Click any project name to drill into its allocation timeline',
    ],
    roles: ['owner', 'admin', 'manager'],
  },
  {
    id: 'reports-revenue-forecast',
    target: 'report-tab:revenue',
    title: 'Revenue Forecast Report',
    description: 'Projects revenue for the next 6 months based on billable allocations. Each project shows estimated monthly revenue calculated from allocation hours and billing rates.',
    listItems: [
      'Monthly revenue comes from billable allocation hours in that month times billing rates',
      'Currency is converted to USD using configured exchange rates',
      'The 6-month total shows the projected revenue across the forecast period',
      "The summary cards below show each month's total with a bar chart comparison",
    ],
    roles: ['owner', 'admin', 'manager'],
  },
  {
    id: 'reports-utilization',
    target: 'report-tab:utilization',
    title: 'Utilization Report',
    description: "Shows how much of each resource's available time is allocated to projects. Use the day, week, month, quarter, or year buttons to change the time period.",
    listItems: [
      "Capacity is the resource's availability multiplied by working days",
      'Allocated hours come from allocations overlapping the selected period',
      'Working days exclude weekends and holidays',
      'Time off days reduce the available working days',
      'Green means 80%+ utilized, blue means 50-80%, orange means under 50%, red means over 100%',
      'Click any resource name to drill into their allocation timeline',
    ],
    roles: ['owner', 'admin', 'manager'],
  },
  {
    id: 'reports-profitability',
    target: 'report-tab:profitability',
    title: 'Project Profitability Report',
    description: 'Shows revenue, cost, profit, and margin percentage for each project. Use this to identify which projects are making money and which are losing it.',
    listItems: [
      'Revenue is calculated from billable allocation hours times billing rates',
      'Cost is calculated from all allocation hours times cost rates',
      'Margin is profit divided by revenue, shown as a percentage bar',
      'Green margin means 20% or higher, orange means low, red means negative',
    ],
    roles: ['owner', 'admin', 'manager'],
  },
  {
    id: 'reports-forecast-vs-actual',
    target: 'report-tab:forecastvactual',
    title: 'Forecast vs Actual Report',
    description: 'Compares forecasted hours from allocations against actual hours logged in timesheets. Use this to see if your team is working more or less than planned.',
    listItems: [
      'Forecast hours come from Forecast-type allocations',
      'Actual hours come from submitted timesheet entries, split into billable and non-billable',
      'Variance is Actual minus Forecast -- positive means more time was spent than planned',
      'The bar shows what percentage of forecast was actually consumed',
    ],
    roles: ['owner', 'admin', 'manager'],
  },
  {
    id: 'reports-nonbillable',
    target: 'report-tab:nonbillable',
    title: 'Non-Billable Report',
    description: 'Shows non-billable hours and their cost per resource and project. Use this to identify where time is being spent that is not generating revenue.',
    listItems: [
      'Non-billable hours come from non-billable allocation hours and non-billable timesheet entries',
      "Cost is calculated from the resource's cost rate",
      'Use this to identify resources or projects with high non-billable overhead',
    ],
    roles: ['owner', 'admin', 'manager'],
  },

  // ─── Gantt Chart ───
  {
    id: 'gantt-chart',
    target: 'report-tab:gantt',
    title: 'Gantt Chart',
    description: 'The Gantt Chart is a visual timeline of your allocations. It is the last tab in the Reports section. It shows bars for each allocation across a calendar grid, color-coded by utilization level.',
    roles: ['owner', 'admin', 'manager'],
  },
  {
    id: 'gantt-resource-view',
    target: 'content:general',
    title: 'Gantt Chart: Resource View',
    description: 'In Resource View, each row is a Resource, with sub-rows showing the projects they are allocated to. This helps you see what each person is working on and when.',
    listItems: [
      'Each resource is a parent row with their projects nested below',
      'Bars show allocation periods with daily hours color-coded',
      'Click the "Add Project" button under a resource to create a new allocation',
      'Right-click any allocation bar to split or delete it',
      'Hover over a bar to see allocation details including rates and period',
    ],
    roles: ['owner', 'admin', 'manager'],
  },
  {
    id: 'gantt-project-view',
    target: 'content:general',
    title: 'Gantt Chart: Project View',
    description: 'In Project View, each row is a Project, with sub-rows showing the resources allocated to it. This helps you see staffing for each project at a glance.',
    listItems: [
      'Each project is a parent row with its resources nested below',
      "Bars show each resource's allocation period on that project",
      'Click the "Add Resource" button under a project to allocate a new resource',
      'Right-click any allocation bar to split or delete it',
      'Use this view to identify projects that need more staffing or have gaps',
    ],
    roles: ['owner', 'admin', 'manager'],
  },
  {
    id: 'gantt-task-assignment',
    target: 'content:general',
    title: 'Managing Allocations from the Gantt Chart',
    description: 'Project Managers can create and modify allocations directly from the Gantt Chart, provided resources are already set up and allocated to projects.',
    listItems: [
      'In Resource View, click "Add Project" under a resource to allocate them to a new project',
      'In Project View, click "Add Resource" under a project to add a new resource allocation',
      'Right-click any allocation bar to split it into two periods or delete it',
      'The allocation form lets you set the role type, dates, daily hours, interval, and billable flag',
      'All changes are saved immediately and reflected across reports and timesheets',
    ],
    roles: ['owner', 'admin', 'manager'],
  },

  // ─── Dashboard ───
  {
    id: 'dashboard',
    target: 'sidebar:dashboard',
    title: 'Dashboard',
    description: 'You are all set! The Dashboard gives you an overview of your organization -- project status, resource utilization, and key metrics. This is your starting point each time you log in.',
    cta: 'Click "Dashboard" in the sidebar to see your organization overview.',
    tabId: 'dashboard',
    roles: ['owner', 'admin', 'manager', 'contributor', 'viewer'],
  },
  {
    id: 'dashboard-resource',
    target: 'content:general',
    title: 'You are all set!',
    description: 'You now know how to set up master data, allocate resources, create tasks, use the Gantt Chart, interpret reports, and log your weekly timesheet within the allowed date windows. If you need a refresher, you can take the tour again anytime from the user menu in the top right corner.',
    roles: ['resource'],
  },
]

export function getTourStepsForRole(userType: UserType): TourStep[] {
  return tourSteps.filter(step => step.roles.includes(userType))
}
